<script setup lang="ts">
  import { watch, computed, ref } from "vue";
  import MarketoInputSyncWrapper from "./marketo-input-sync-wrapper.vue";
  import EclipseInput from "./eclipse-input.vue";
  import { UserMarketoInputName, type MarketoVals } from "../types/definitions/inputs/marketo-input-names-and-values.js";
  import { useFormServices, type InputState } from "../composables/use-form-services.js";
  import { useFormUserInputValues } from "../composables/use-form-user-input-values.js";

  import { useVModel, refDefault } from "@vueuse/core";
  import { useSiteLanguage } from '../composables/use-site-language.js';
  import type { Nil } from "@mcwd/typescript-type-guards";
  import type { MarketoUserInputMeta } from "../types/definitions/inputs/marketo-input-meta.js";

  export interface StateOrTerritoryInputProps {
    formInputMeta: MarketoUserInputMeta,
    inputState: InputState
    modelValue: string | Nil;
    dropdownOptionKeys: readonly string[],
    dropdownTranslationsNamespace: string;
  }

  const props = defineProps<StateOrTerritoryInputProps>();

  const { siteCountryCode } = useSiteLanguage();

  const emit = defineEmits<(e: 'update:modelValue', modelValue: StateOrTerritoryInputProps["modelValue"]) => void>();


  const proxyModelValue = refDefault(useVModel(props, 'modelValue', emit), null);

  const { FormSettings } = useFormServices({ callerLabel: "state-or-territory-selector.vue" });

  const enabledFields = FormSettings.enabledFields.FullGate satisfies readonly string[];
  type EnabledField = typeof enabledFields[number];


  function getEnabledUserFields() {
    return enabledFields.filter(f => (UserMarketoInputName as readonly string[]).includes(f)) as Extract<EnabledField, UserMarketoInputName>[];
  }
  // Currently only handles marketo field names
  const anyEnabledUserField = getEnabledUserFields();
  type AnyEnabledUserField = typeof anyEnabledUserField[number];

  function isFieldEnabled(field: UserMarketoInputName ){
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    return anyEnabledUserField.includes(field as AnyEnabledUserField);
  }

  type UserMarketoValues = { [Key in AnyEnabledUserField]: Required<MarketoVals>[Key] };

  const { userInputValues } = useFormUserInputValues<AnyEnabledUserField, UserMarketoValues>({ callerLabel: "state-or-territory-selector.vue"});

  watch(() => siteCountryCode, val  =>{
    console.log("SiteCountryCode changed: ", val);
  });

  watch(() => userInputValues.Country, val  =>{
    console.log("Country changed: ", val);
  });

  function checkStateOrTerritoryIsRequired(){
    if (!isFieldEnabled('State') || !isFieldEnabled('Country')) {
      return false;
    }
    return userInputValues.Country === 'United States' || ((userInputValues.Country == '' || userInputValues.Country == null) && siteCountryCode === 'us');
  }
  const stateOrTerritoryIsRequired = computed(() => {
    return checkStateOrTerritoryIsRequired();
  });

  const stateOrTerritoryUserValue = ref<string | null>(null);


  watch(() => stateOrTerritoryIsRequired.value, (currentlyRequired: boolean, prevRequired: boolean) => {
    // Switch from state/territory not required to required
    if (prevRequired === false && currentlyRequired === true) {
      // Restore user value
      proxyModelValue.value = stateOrTerritoryUserValue.value;
    }
    // Switch from required to not required
    else if (prevRequired === true && currentlyRequired === false) {
      // Save user value
      stateOrTerritoryUserValue.value = (proxyModelValue.value);

      // Set value to null when value is not required
      proxyModelValue.value = null;
    }
  });


</script>

<template>
  <MarketoInputSyncWrapper v-model="proxyModelValue" :marketo-input-name="formInputMeta.marketoName">
    <EclipseInput 
      v-if="(isFieldEnabled('State') && isFieldEnabled('Country')) && (userInputValues.Country === 'United States' || ((userInputValues.Country == ''  || userInputValues.Country == null) && siteCountryCode === 'us'))"
      v-model="proxyModelValue"
      :without-sync-wrapper="true"
      :dropdown-option-keys="dropdownOptionKeys"
      :dropdown-translations-namespace="dropdownTranslationsNamespace"
      :form-input-meta="formInputMeta"
      :input-state="inputState"
    />
  </MarketoInputSyncWrapper>
</template>
